import { IInventoryPurchaseOrderProductSearchResponseData } from "@/api_framework/api_modals/Inventory";
import { ITransferOrderSearchFilterBody } from "@/api_framework/api_modals/TransferOrder";
import { SectionSearchDataTypes } from "@/app/hooks/api_hooks/emr/useEMRSectionSearchAPI";
import { useTransferOrderSearchAPI } from "@/app/hooks/api_hooks/Inventory/ClinicToClinicTransfer/useTransferOrderSearchAPI";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";

export type EMRSectionSearchResultType = {
  name: string;
  id: string;
};

const ProductSearchByClinicId: React.FC<{
  searchType: SectionSearchDataTypes;
  flexibleAdd: boolean;
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  isInputEnabled?: boolean;
  clinicId: string | Array<string>;
  brandIds: Array<string>;
  subCategoryIds: Array<string>;
  transferDirection?: string;
  onSelectItem?: (item: IInventoryPurchaseOrderProductSearchResponseData) => void;
}> = ({
  searchType,
  containerClassName,
  inputContainerClassName,
  clinicId,
  brandIds,
  subCategoryIds,
  transferDirection,
  onSelectItem,
  ...props
}) => {
  const { execute: searchInSection, searchData, clearSearchResults } = useTransferOrderSearchAPI();
  const [selected, setSelected] = useState<IInventoryPurchaseOrderProductSearchResponseData | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      const clinicIds = Array.isArray(clinicId) ? clinicId.join(",") : clinicId;
      const filterBody: ITransferOrderSearchFilterBody = {
        clinic_id: clinicIds,
        transfer_inventory_type: `${location.pathname.split("/")[3].split("-")[1]}_inventory`,
      };
      if (Array.isArray(brandIds) && brandIds.length > 0) {
        filterBody.brand_ids = brandIds;
      }
      if (Array.isArray(subCategoryIds) && subCategoryIds.length > 0) {
        filterBody.sub_category_ids = subCategoryIds;
      }
      if (transferDirection) {
        filterBody.transfer_direction = transferDirection;
      }
      searchInSection(filterBody, debouncedQuery);
    }
  }, [debouncedQuery, clinicId, brandIds, subCategoryIds, transferDirection]);

  const reducedSearchResults: EMRSectionSearchResultType[] =
    searchData?.map((item) => ({
      name: item.vetic_variant_name,
      id: item.vetic_variant_id,
    })) || [];

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={(item) => {
          setSelected(item);
          if (onSelectItem) {
            onSelectItem(item);
          }
        }}
      >
        <div className="w-full relative mt-1">
          <div className="relative w-full border border-gray-200 cursor-default overflow-hidden rounded-lg bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button
                  className="p-2"
                  onClick={() => {
                    setQuery("");
                    clearSearchResults();
                    setSelected(null);
                    if (onSelectItem) onSelectItem(null);
                  }}
                >
                  <XMarkIcon className="w-4 h-4 text-red-500" />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  placeholder={props?.placeholder ?? "Search By Product"}
                  className="w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSelected(null); // optional: clear selected item while typing
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Backspace" && query === "") {
                      setSelected(null);
                      if (onSelectItem) {
                        onSelectItem(null);
                      }
                    }
                  }}
                  readOnly={props?.isInputEnabled}
                  value={selected ? selected.name : query}
                />
              </Combobox.Button>
            </div>

            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
              <div className="w-full pl-4">
                {props.isInputEnabled ? (
                  <p className="text-xs text-red-400 font-medium py-3">
                    Please Select Vendor Branch And Clinic Before Product Search
                  </p>
                ) : (
                  <span className="text-xs">
                    Search results for <span className="text-blue-600 font-semibold">{query}</span>
                  </span>
                )}
              </div>
              {reducedSearchResults?.length === 0 && query !== "" ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                  <p>No Result Found.</p>
                </div>
              ) : (
                reducedSearchResults?.map((result, idx) => (
                  <Combobox.Option
                    key={`${result}:${idx}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${
                        active
                          ? props?.itemActiveClassName
                            ? props?.itemActiveClassName
                            : "bg-teal-500 text-white"
                          : "text-gray-900"
                      }`
                    }
                    value={result}
                  >
                    {({ selected, active }) => (
                      <div className="flex justify-between items-center">
                        <span
                          data-tooltip-id={`${searchType}:option`}
                          data-tooltip-content={result?.name}
                          className={`block ${selected ? "font-medium" : "font-normal"} ${searchType === "Food" || searchType === "Medicines" ? "truncate w-72" : ""} `}
                        >
                          {result.name}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? "text-white" : "text-teal-600"
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </div>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default ProductSearchByClinicId;
